import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Search
} from 'lucide-react';
import { useConfirmDialog } from '@/components/useConfirmDialog';

const initialForm = {
  name: '',
  email: '',
  phone: '',
  tipo_colaborador: 'professor',
  percentual_sociedade: '',
  status: 'ativo'
};

export default function ColaboradoresPage() {
  const [colaboradores, setColaboradores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);

  const { confirm, ConfirmDialog } = useConfirmDialog();

  useEffect(() => {
    api
      .get('/gestor/colaboradores')
      .then(r => setColaboradores(r.data))
      .catch(() => toast.error('Erro ao carregar'))
      .finally(() => setLoading(false));
  }, []);

  const refresh = async () => {
    const r = await api.get('/gestor/colaboradores');
    setColaboradores(r.data);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      form.tipo_colaborador === 'socio'
      && (
        form.percentual_sociedade === ''
        || Number.isNaN(Number(form.percentual_sociedade))
        || Number(form.percentual_sociedade) < 0
        || Number(form.percentual_sociedade) > 100
      )
    ) {
      toast.error(
        'Informe um percentual da sociedade entre 0 e 100%'
      );
      return;
    }

    setSaving(true);

    try {
      if (editing) {
        const data = {
          name: form.name,
          email: form.email,
          phone: form.phone,
          status: form.status
        };

        if (form.tipo_colaborador === 'socio') {
          data.percentual_sociedade =
            Number(form.percentual_sociedade);
        }

        await api.put(
          `/gestor/colaboradores/${editing.id}`,
          data
        );

        toast.success('Colaborador atualizado');
      } else {
        const data = {
          name: form.name,
          email: form.email,
          phone: form.phone,
          tipo_colaborador: form.tipo_colaborador
        };

        if (form.tipo_colaborador === 'socio') {
          data.percentual_sociedade =
            Number(form.percentual_sociedade);
        }

        await api.post(
          '/gestor/colaboradores',
          data
        );

        toast.success('Colaborador criado');
      }

      setDialogOpen(false);
      setEditing(null);
      setForm(initialForm);

      await refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (
      !await confirm(
        'Remover Colaborador',
        'Tem certeza que deseja remover este colaborador?'
      )
    ) {
      return;
    }

    try {
      await api.delete(
        `/gestor/colaboradores/${id}`
      );

      toast.success('Colaborador removido');
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm(initialForm);
    setDialogOpen(true);
  };

  const openEdit = (c) => {
    setEditing(c);

    setForm({
      name: c.name || '',
      email: c.email || '',
      phone: c.phone || '',
      tipo_colaborador:
        c.tipo_colaborador || c.role || 'professor',
      percentual_sociedade:
        c.percentual_sociedade ?? '',
      status: c.status || 'ativo'
    });

    setDialogOpen(true);
  };

  const tipoLabel = {
    professor: 'Professor',
    financeiro: 'Financeiro',
    socio: 'Sócio'
  };

  const filtered = colaboradores.filter(
    c =>
      c.name
        ?.toLowerCase()
        .includes(search.toLowerCase())
      ||
      c.email
        ?.toLowerCase()
        .includes(search.toLowerCase())
  );

  return (
    <div
      className="space-y-6 animate-fadeIn"
      data-testid="colaboradores-page"
    >
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-3xl font-black font-['Barlow_Condensed'] tracking-tight uppercase text-white">
          COLABORADORES
        </h1>

        <Button
          onClick={openCreate}
          className="bg-[#007AFF] hover:bg-[#0066DD]"
          data-testid="add-colaborador-button"
        >
          <Plus className="w-4 h-4 mr-2" />
          Novo Colaborador
        </Button>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />

        <Input
          className="pl-10 bg-[#0A0A0A] border-[#27272A]"
          placeholder="Buscar..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      <Card className="bg-[#141414] border-[#27272A]">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-[#27272A] hover:bg-transparent">
                <TableHead className="text-zinc-400">
                  Nome
                </TableHead>

                <TableHead className="text-zinc-400">
                  Email
                </TableHead>

                <TableHead className="text-zinc-400">
                  Tipo
                </TableHead>

                <TableHead className="text-zinc-400">
                  Status
                </TableHead>

                <TableHead className="text-zinc-400 text-right">
                  Ações
                </TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="text-center text-zinc-500 py-8"
                  >
                    Carregando...
                  </TableCell>
                </TableRow>
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="text-center text-zinc-500 py-8"
                  >
                    Nenhum colaborador cadastrado
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map(c => {
                  const tipo =
                    c.tipo_colaborador || c.role;

                  return (
                    <TableRow
                      key={c.id}
                      className="border-[#27272A] hover:bg-[#1C1C1E]"
                    >
                      <TableCell className="text-white font-medium">
                        {c.name}
                      </TableCell>

                      <TableCell className="text-zinc-400">
                        {c.email}
                      </TableCell>

                      <TableCell>
                        <span className="text-xs px-2 py-1 rounded-md font-medium bg-[#007AFF]/10 text-[#007AFF]">
                          {tipoLabel[tipo] || tipo}

                          {tipo === 'socio' && (
                            <>
                              {' · '}
                              {Number(
                                c.percentual_sociedade || 0
                              ).toLocaleString(
                                'pt-BR',
                                {
                                  maximumFractionDigits: 2
                                }
                              )}
                              %
                            </>
                          )}
                        </span>
                      </TableCell>

                      <TableCell>
                        <span
                          className={`text-xs px-2 py-1 rounded-md font-medium ${
                            c.status === 'ativo'
                              ? 'bg-green-400/10 text-green-400'
                              : 'bg-red-400/10 text-red-400'
                          }`}
                        >
                          {c.status}
                        </span>
                      </TableCell>

                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openEdit(c)}
                          className="text-zinc-400 hover:text-white"
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>

                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDelete(c.id)}
                          className="text-zinc-400 hover:text-red-400"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
      >
        <DialogContent className="bg-[#141414] border-[#27272A] sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed'] text-xl tracking-tight uppercase">
              {editing
                ? 'Editar Colaborador'
                : 'Novo Colaborador'}
            </DialogTitle>
          </DialogHeader>

          <form
            onSubmit={handleSubmit}
            className="space-y-4"
          >
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">
                Nome
              </Label>

              <Input
                value={form.name}
                onChange={e =>
                  setForm({
                    ...form,
                    name: e.target.value
                  })
                }
                className="bg-[#0A0A0A] border-[#27272A]"
                required
                data-testid="colaborador-form-name"
              />
            </div>

            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">
                Email
              </Label>

              <Input
                type="email"
                value={form.email}
                onChange={e =>
                  setForm({
                    ...form,
                    email: e.target.value
                  })
                }
                className="bg-[#0A0A0A] border-[#27272A]"
                required
              />
            </div>

            {!editing && (
              <p className="text-xs text-zinc-500">
                Senha padrão: 12345678
              </p>
            )}

            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">
                Telefone
              </Label>

              <Input
                value={form.phone}
                onChange={e =>
                  setForm({
                    ...form,
                    phone: e.target.value
                  })
                }
                className="bg-[#0A0A0A] border-[#27272A]"
              />
            </div>

            {!editing && (
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">
                  Tipo
                </Label>

                <Select
                  value={form.tipo_colaborador}
                  onValueChange={v =>
                    setForm({
                      ...form,
                      tipo_colaborador: v,
                      percentual_sociedade:
                        v === 'socio'
                          ? form.percentual_sociedade
                          : ''
                    })
                  }
                >
                  <SelectTrigger className="bg-[#0A0A0A] border-[#27272A]">
                    <SelectValue />
                  </SelectTrigger>

                  <SelectContent className="bg-[#141414] border-[#27272A]">
                    <SelectItem value="professor">
                      Professor
                    </SelectItem>

                    <SelectItem value="financeiro">
                      Financeiro
                    </SelectItem>

                    <SelectItem value="socio">
                      Sócio
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {form.tipo_colaborador === 'socio' && (
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">
                  Participação nos lucros (%)
                </Label>

                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={form.percentual_sociedade}
                  onChange={e =>
                    setForm({
                      ...form,
                      percentual_sociedade:
                        e.target.value
                    })
                  }
                  className="bg-[#0A0A0A] border-[#27272A]"
                  required
                  data-testid="colaborador-percentual-sociedade"
                />

                <p className="text-[11px] text-zinc-500 mt-1">
                  Percentual aplicado sobre o lucro após os custos.
                </p>
              </div>
            )}

            {editing && (
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">
                  Status
                </Label>

                <Select
                  value={form.status}
                  onValueChange={v =>
                    setForm({
                      ...form,
                      status: v
                    })
                  }
                >
                  <SelectTrigger className="bg-[#0A0A0A] border-[#27272A]">
                    <SelectValue />
                  </SelectTrigger>

                  <SelectContent className="bg-[#141414] border-[#27272A]">
                    <SelectItem value="ativo">
                      Ativo
                    </SelectItem>

                    <SelectItem value="inativo">
                      Inativo
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                onClick={() =>
                  setDialogOpen(false)
                }
                className="text-zinc-400"
              >
                Cancelar
              </Button>

              <Button
                type="submit"
                disabled={saving}
                className="bg-[#007AFF] hover:bg-[#0066DD]"
                data-testid="colaborador-form-submit"
              >
                {saving
                  ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  )
                  : (
                    editing
                      ? 'Salvar'
                      : 'Criar'
                  )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDialog />
    </div>
  );
}
